/**
 * VerdictCards — renders 3 verdict cards (Scale/Fix/Kill) with count, percentage,
 * trend delta, 4-point sparkline. Cards are independently expandable to show
 * VerdictProfile (multiple can be open at once — FR-11).
 */

import { useState } from 'react';
import { LineChart, Line } from 'recharts';

import { ComparisonBadge } from '@/components/dashboard/shared/ComparisonBadge';
import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { useVerdictSummary } from '@/hooks/useDashboard';
import { fmtPct } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { VerdictCard } from '@/types/dashboard';

import { VerdictProfile } from './VerdictProfile';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

const VERDICT_BG: Record<string, string> = {
  Scale: 'rgba(35, 193, 107, 0.05)',
  Fix: 'rgba(255, 176, 32, 0.05)',
  Kill: 'rgba(255, 77, 79, 0.05)',
};

// ============================================================================
// SINGLE CARD
// ============================================================================

interface CardProps {
  card: VerdictCard;
  isExpanded: boolean;
  onToggle: () => void;
  profiles: ReturnType<typeof useVerdictSummary>['data'];
}

function SingleVerdictCard({ card, isExpanded, onToggle, profiles: _profiles }: CardProps) {
  const color = VERDICT_COLORS[card.verdict] ?? '#8B95A7';
  const bgColor = VERDICT_BG[card.verdict];
  const sparklineData = card.sparkline.map((v, i) => ({ i, value: v }));


  return (
    <div
      onClick={onToggle}
      className={cn(
        'rounded-xl p-4 bg-surface shadow-sm cursor-pointer transition-all border border-border-subtle',
        'hover:shadow-md',
        isExpanded && 'ring-2'
      )}
      style={{
        backgroundColor: isExpanded ? bgColor : undefined,
        borderColor: isExpanded ? color : undefined,
      }}
    >
      {/* Colored top bar */}
      <div className="h-1 rounded-full mb-3" style={{ backgroundColor: color }} />

      <div className="flex items-start justify-between gap-2">
        {/* Left: verdict label + count */}
        <div>
          <div className="text-xs font-medium text-secondary uppercase tracking-wide">
            {card.verdict}
          </div>
          <div
            className="text-3xl font-bold leading-none mt-0.5"
            style={{ color }}
          >
            {card.count}
          </div>
          <div className="text-lg font-semibold text-primary mt-0.5">
            {fmtPct(card.percentage)}
          </div>
          <div className="mt-1">
            <ComparisonBadge
              delta={card.trendDelta ?? 0}
              unit="%"
              higherIsBetter={card.verdict === 'Scale'}
            />
          </div>
        </div>

        {/* Right: sparkline (4 points, no axes, no dots) */}
        {sparklineData.length > 0 && (
          <div className="flex-shrink-0">
            <LineChart width={80} height={24} data={sparklineData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </div>
        )}
      </div>

      {/* Expand hint */}
      <div className="text-xs text-secondary mt-2 text-right">
        {isExpanded ? 'collapse' : 'profile'}
      </div>

      {/* Expanded: VerdictProfile — always mounted, toggled via CSS to avoid re-fetch */}
      <div
        className={cn('transition-all', isExpanded ? 'block' : 'hidden')}
        onClick={(e) => e.stopPropagation()}
      >
        <VerdictProfile verdict={card.verdict} />
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function VerdictCards() {
  const { filters } = useDashboardContext();
  const { data, isLoading, error } = useVerdictSummary(filters);
  const [expandedVerdicts, setExpandedVerdicts] = useState<Set<string>>(new Set());

  if (isLoading || !data) return null;
  if (error) return null;

  const cards = data.verdictCards;
  if (!cards || cards.length === 0) return null;

  const csvHeaders = ['Verdict', 'Count', 'Percentage', 'Trend'];
  const csvRows = cards.map((card) => [
    card.verdict,
    card.count,
    card.percentage,
    card.trendDelta ?? 0,
  ]);

  function handleToggle(verdict: string) {
    setExpandedVerdicts((prev) => {
      const next = new Set(prev);
      if (next.has(verdict)) {
        next.delete(verdict);
      } else {
        next.add(verdict);
      }
      return next;
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-primary">Verdict Distribution</div>
        <CsvExport headers={csvHeaders} rows={csvRows} filename="verdict-distribution" />
      </div>
      <div className="grid grid-cols-3 gap-3">
      {cards.map((card) => (
        <SingleVerdictCard
          key={card.verdict}
          card={card}
          isExpanded={expandedVerdicts.has(card.verdict)}
          onToggle={() => handleToggle(card.verdict)}
          profiles={data}
        />
      ))}
      </div>
    </div>
  );
}
